import { useEffect, useMemo, useState } from "react"
import { Formik, Form, Field, ErrorMessage } from "formik"
import { array, date, mixed, object, string } from "yup"
import Select from "react-select"
import { X } from "@phosphor-icons/react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowDown,
  faArrowUp,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import Modal from "../../components/Modal"
import TextField from "../../components/TextField"
import DatePicker from "../../components/DatePicker"
import {
  useCreateEventQrCode,
  useEventQrCode,
  useEventVideos,
  useUpdateEventQrCode,
} from "../../hooks/useEventQr"
import { ToggleSwitch } from "./shared"

const validationSchema = object().shape({
  eventName: string().label("Event name").trim().required().max(200),
  description: string().label("Description").trim().max(500),
  startsAt: date().nullable(),
  expiresAt: date()
    .nullable()
    .test(
      "after-start",
      "Expiry must be after the start date",
      function isAfterStart(expiresAt) {
        const { startsAt } = this.parent
        return !expiresAt || !startsAt || expiresAt > startsAt
      }
    ),
  isActive: mixed(),
  videos: array().min(1, "Add at least one video"),
})

const toInitialValues = (event) => ({
  eventName: event?.eventName || "",
  description: event?.description || "",
  startsAt: event?.startsAt ? new Date(event.startsAt) : null,
  expiresAt: event?.expiresAt ? new Date(event.expiresAt) : null,
  isActive: event ? event.isActive : true,
  videos:
    event?.videos?.map((video) => ({
      videoId: video.videoId,
      title: video.title,
      isActive: video.isActive,
    })) || [],
})

const move = (list, from, to) => {
  const next = [...list]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}

const FieldLabel = ({ children, hint }) => (
  <label className="ml-1 mb-1 block text-sm">
    {children}
    {hint && <span className="ml-1 text-xs text-gray-400">{hint}</span>}
  </label>
)

const VideoList = ({ videos, onChange }) => {
  if (!videos.length) {
    return (
      <p className="rounded-lg border border-dashed border-gray-300 px-3 py-4 text-center text-xs text-gray-400">
        No videos yet. Add at least one from the library.
      </p>
    )
  }

  return (
    <ol className="divide-y divide-gray-100 rounded-lg border border-gray-200">
      {videos.map((video, index) => (
        <li
          key={video.videoId}
          className="flex items-center gap-2 px-3 py-2 text-sm"
        >
          <span className="w-5 shrink-0 text-xs tabular-nums text-gray-400">
            {index + 1}.
          </span>
          <span className="min-w-0 flex-1 truncate" title={video.title}>
            {video.title}
          </span>
          {!video.isActive && (
            <span
              className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-700"
              title="Turned off in the library. It stays on this event until you remove it."
            >
              Off in library
            </span>
          )}
          <button
            aria-label="Move up"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
            disabled={index === 0}
            type="button"
            onClick={() => onChange(move(videos, index, index - 1))}
          >
            <FontAwesomeIcon className="h-3 w-3" icon={faArrowUp} />
          </button>
          <button
            aria-label="Move down"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
            disabled={index === videos.length - 1}
            type="button"
            onClick={() => onChange(move(videos, index, index + 1))}
          >
            <FontAwesomeIcon className="h-3 w-3" icon={faArrowDown} />
          </button>
          <button
            aria-label="Remove"
            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
            type="button"
            onClick={() => onChange(videos.filter((_, i) => i !== index))}
          >
            <FontAwesomeIcon className="h-3 w-3" icon={faXmark} />
          </button>
        </li>
      ))}
    </ol>
  )
}

/*
  Create or edit an event QR code. The QR itself isn't shown here: it opens
  from the list's QR button and the results page, both drawn from the saved
  PublicUuid, so it is always the same image whatever is edited here.
*/
const EventQrCodeModal = ({ isOpen, eventId, onClose }) => {
  const [currentId, setCurrentId] = useState(eventId)
  useEffect(() => {
    if (isOpen) setCurrentId(eventId)
  }, [isOpen, eventId])

  const { data: event, isLoading } = useEventQrCode(currentId)
  const { data: library = [] } = useEventVideos()
  const createMutation = useCreateEventQrCode()
  const updateMutation = useUpdateEventQrCode()

  const isEditing = !!currentId
  const initialValues = useMemo(
    () => toInitialValues(isEditing ? event : null),
    [isEditing, event]
  )

  const handleSubmit = async (values) => {
    const payload = {
      eventName: values.eventName.trim(),
      description: values.description.trim() || null,
      startsAt: values.startsAt ? values.startsAt.toISOString() : null,
      expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
      isActive: values.isActive,
      videoIds: values.videos.map((video) => video.videoId),
    }

    // Failures are toasted by the mutation hooks; the modal stays open.
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: currentId, ...payload })
      } else {
        await createMutation.mutateAsync(payload)
      }
      onClose()
    } catch {
      // Handled by the mutation's onError.
    }
  }

  return (
    <Modal className="max-w-3xl" isOpen={isOpen} onClose={onClose}>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">
          {isEditing ? "Edit event QR code" : "New event QR code"}
        </h2>
        <button
          className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          type="button"
          onClick={onClose}
        >
          <X size={20} />
        </button>
      </div>

      {isEditing && isLoading ? (
        <div className="py-16 text-center text-sm text-gray-500">Loading…</div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, isSubmitting, errors }) => {
            const chosenIds = new Set(values.videos.map((v) => v.videoId))
            const options = library
              .filter(
                (video) => video.isActive && !chosenIds.has(video.videoId)
              )
              .map((video) => ({
                value: video.videoId,
                label: video.speciality
                  ? `${video.title} · ${video.speciality}`
                  : video.title,
                video,
              }))

            return (
              <Form className="space-y-4">
                <TextField
                  autofocus={!isEditing}
                  label="Event name"
                  name="eventName"
                  placeholder="e.g. PI Summit Jacksonville 2026"
                />

                <div>
                  <FieldLabel hint="optional · shown under the event name">
                    Description
                  </FieldLabel>
                  <Field
                    as="textarea"
                    className="h-20 w-full resize-none rounded-lg border border-slate-300 p-2 text-sm text-gray-900 outline-0 placeholder:text-gray-400 focus:border-secondary"
                    maxLength={500}
                    name="description"
                    placeholder="e.g. Spine education videos from the Expert Radiology booth."
                  />
                  <div className="flex justify-between">
                    <ErrorMessage name="description">
                      {(msg) => (
                        <p className="ml-2 mt-1 text-xs text-rose-500">{msg}</p>
                      )}
                    </ErrorMessage>
                    <span className="ml-auto text-xs text-gray-400">
                      {values.description.length}/500
                    </span>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <FieldLabel hint="optional">Starts</FieldLabel>
                    <DatePicker
                      isClearable
                      showTimeSelect
                      dateFormat="MM/dd/yyyy h:mm aa"
                      error={errors.startsAt}
                      placeholder="Live right away"
                      value={values.startsAt}
                      wrapperClassName="w-full flex items-center border rounded-lg bg-white p-1.5"
                      onChange={(value) => setFieldValue("startsAt", value)}
                    />
                  </div>
                  <div>
                    <FieldLabel hint="optional">Expires</FieldLabel>
                    <DatePicker
                      isClearable
                      showTimeSelect
                      dateFormat="MM/dd/yyyy h:mm aa"
                      error={errors.expiresAt}
                      minDate={values.startsAt || undefined}
                      placeholder="Never expires"
                      value={values.expiresAt}
                      wrapperClassName="w-full flex items-center border rounded-lg bg-white p-1.5"
                      onChange={(value) => setFieldValue("expiresAt", value)}
                    />
                  </div>
                </div>
                <p className="-mt-2 ml-1 text-xs text-gray-400">
                  Before the start, the page shows a countdown. Times are in
                  your time zone.
                </p>

                {/* A past expiry (and so a past start, which must come
                      before it) ends the event on save. Allowed — an admin
                      may be recording a past event — but never by surprise. */}
                {values.expiresAt && values.expiresAt <= new Date() && (
                  <div
                    className="-mt-1 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                    role="alert"
                  >
                    <FontAwesomeIcon
                      className="mt-0.5 h-3.5 w-3.5 shrink-0"
                      icon={faTriangleExclamation}
                    />
                    <span className="font-semibold">
                      This QR code is already expired.
                    </span>
                  </div>
                )}

                <div>
                  <FieldLabel>Videos</FieldLabel>
                  <Select
                    isSearchable
                    maxMenuHeight={220}
                    noOptionsMessage={() => "No more active videos"}
                    options={options}
                    placeholder="Add a video from the library…"
                    value={null}
                    onChange={(option) =>
                      option &&
                      setFieldValue("videos", [
                        ...values.videos,
                        {
                          videoId: option.video.videoId,
                          title: option.video.title,
                          isActive: option.video.isActive,
                        },
                      ])
                    }
                  />
                  <div className="mt-2">
                    <VideoList
                      videos={values.videos}
                      onChange={(videos) => setFieldValue("videos", videos)}
                    />
                  </div>
                  <ErrorMessage name="videos">
                    {(msg) => (
                      <p className="text-rose-500 text-xs ml-2 mt-1">{msg}</p>
                    )}
                  </ErrorMessage>
                </div>

                <div className="flex items-center gap-3">
                  <ToggleSwitch
                    checked={values.isActive}
                    label="Active"
                    onChange={() => setFieldValue("isActive", !values.isActive)}
                  />
                  <span className="text-sm text-gray-700">
                    {values.isActive
                      ? "On — the QR code works"
                      : "Off — the QR code shows “not available”"}
                  </span>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-slate-50"
                    disabled={isSubmitting}
                    type="button"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    className="flex-1 rounded-lg bg-secondary px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-secondary_dark disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={isSubmitting}
                    type="submit"
                  >
                    {isSubmitting
                      ? "Saving…"
                      : isEditing
                        ? "Save changes"
                        : "Create QR code"}
                  </button>
                </div>
              </Form>
            )
          }}
        </Formik>
      )}
    </Modal>
  )
}

export default EventQrCodeModal
